import { Injectable } from '@angular/core';

import {
  AnalyticsDigitalData,
  AnalyticsDigitalDataModel,
} from '@digital-platform/analytics/domain';
import { WindowService } from '@digital-platform/shared/services';
import { LoginSession, ProfileSettings } from '@digital-platform/users/domain';
import { WasteProfile } from '@digital-platform/waste-profiles/domain';
import { WasteServiceRequest } from '@digital-platform/waste-service-requests/domain';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  protected digitalData: AnalyticsDigitalData;
  private readonly TRACK_PAGE_VIEW = 'pageView';
  constructor(protected windowService: WindowService) {
    windowService.nativeWindow.digitalData =
      windowService.nativeWindow.digitalData ||
      AnalyticsDigitalDataModel.DEFAULT_CONFIG;
    this.digitalData = windowService.nativeWindow.digitalData;
  }

  public registerPageName(pageName: string): void {
    this.digitalData.pageName = pageName || '';
    this.satelliteTrack(this.TRACK_PAGE_VIEW);
  }

  public satelliteTrack(trackId: string): void {
    // make sure adobe has loaded, then trigger
    if (!this.windowService.nativeWindow['_satellite']) {
      const checkInterval = setInterval(() => {
        if (this.windowService.nativeWindow['_satellite']) {
          clearInterval(checkInterval);
          this.windowService.nativeWindow['_satellite'].track(trackId);
        }
      }, 100);
    } else {
      this.windowService.nativeWindow['_satellite'].track(trackId);
    }
  }

  public registerUserData(session: LoginSession): void {
    const { user } = session;

    if (!user) {
      return;
    }

    this.digitalData.userData = {
      attributes: {
        name: user.name || '',
        email: user.email || '',
        title: user.title || '',
        company: user.company || '',
      },
    };
  }

  public registerWasteProfileData(wasteProfile: WasteProfile): void {
    if (!wasteProfile) {
      return;
    }

    const wasteProfileData = {
      id: wasteProfile.id || '',
      customerName: wasteProfile.customerName || '',
      profileNumber: wasteProfile.number || '',
      wasteStreamName: wasteProfile.name || '',
      isDuplicate: wasteProfile.isDuplicate || false,
      duplicatedFromId: wasteProfile.duplicatedFromId || '',
      dateSubmitted: new Date(),
      submittedBy: wasteProfile?.signatures[0]?.name || '',
    };
    this.digitalData.wasteProfileData = wasteProfileData;
  }

  public registerChangeWasteProfileData(wasteProfile: WasteProfile): void {
    if (!wasteProfile) {
      return;
    }

    const wasteProfileChangeData = {
      id: wasteProfile.id || '',
      customerName: wasteProfile.customerName || '',
      profileNumber: wasteProfile.number || '',
      wasteStreamName: wasteProfile.name || '',
      dateSubmitted:
        new Date(wasteProfile.pendingChangeRequest.signedDate) || '',
      submittedBy: wasteProfile.pendingChangeRequest.signedName || '',
    };

    this.digitalData.wasteProfileChangeData = wasteProfileChangeData;
  }

  public registerRecertifyWasteProfileData(wasteProfile: WasteProfile): void {
    if (!wasteProfile) {
      return;
    }

    const wasteProfileRecertifyData = {
      id: wasteProfile.id || '',
      customerName: wasteProfile.customerName || '',
      profileNumber: wasteProfile.number || '',
      wasteStreamName: wasteProfile.name || '',
      dateSubmitted:
        new Date(wasteProfile.pendingRecertification?.signedDate) || '',
      submittedBy: wasteProfile.pendingRecertification?.signedName || '',
    };

    this.digitalData.wasteProfileRecertifyData = wasteProfileRecertifyData;
  }

  public registerWasteProfileAdminStatusChangeData(
    wasteProfile: WasteProfile
  ): void {
    if (!wasteProfile) {
      return;
    }

    const wasteProfileAdminStatusChangeData = {
      customerName: wasteProfile.customerName || '',
      profileNumber: wasteProfile.number || '',
      wasteStreamName: wasteProfile.name || '',
      dateStatusChanged: new Date(wasteProfile.lastModifiedDate) || '',
      submittedBy: wasteProfile.lastModifiedBy || '',
      status: wasteProfile.status || '',
    };

    this.digitalData.wasteProfileAdminStatusChangeData =
      wasteProfileAdminStatusChangeData;
  }

  public registerWasteServiceRequestData(
    serviceRequest: WasteServiceRequest
  ): void {
    if (!serviceRequest) {
      return;
    }

    const serviceRequestData = {
      customerName: serviceRequest.customerName || '',
      serviceRequestType: serviceRequest.type || '',
      wasteStreamName: serviceRequest.wasteStreamName || '',
      dateSubmitted: new Date(serviceRequest.lastModifiedDate) || '',
      submittedBy: serviceRequest.lastModifiedBy || '',
    };

    this.digitalData.wasteServiceRequestData = serviceRequestData;
  }

  public registerUserProfileData(profileSettings: ProfileSettings): void {
    const { profileInfo } = profileSettings;

    if (!profileInfo) {
      return;
    }

    this.digitalData.userProfileData = {
      attributes: {
        customerName: `${profileInfo.firstName} ${profileInfo.lastName}` || '',
        email: profileInfo.email || '',
        title: profileInfo.title || '',
        phoneNumber: profileInfo.phoneNumber || '',
      },
    };
  }
  public registerWasteProfileAdminMetaData(wasteProfile: WasteProfile): void {
    if (!wasteProfile) {
      return;
    }
    const wasteProfileAdminMetaData = {
      customerName: wasteProfile.customerName || '',
      profileNumber: wasteProfile.number || '',
      wasteStreamName: wasteProfile.name || '',
      dateChanged: new Date(wasteProfile.lastModifiedDate) || '',
      changedBy: wasteProfile.lastModifiedBy || '',
      status: wasteProfile.internalStatus || '',
    };

    this.digitalData.wasteProfileAdminMetaData = wasteProfileAdminMetaData;
  }
}
